<template>
  <div>
    <app-table v-bind="$attrs" class="w-full">
      <template v-slot:table-body>
        <tr
          v-for="receipt in currentUser.role.is_office_worker === 0 || isInput
            ? filteredReceipts
            : filteredReceiptsBySelect"
          :key="'receipts-table-' + receipt.id"
          class="border border-collapse border-dashed border-green border-t-0 border-l-0 border-r-0 h-12 hover:bg-green-lightest"
        >
          <td width="160px">
            <app-receipt-status-indicator
              class="mb-8"
              color="green"
              :receiptStatus="receipt.option.viewType"
              @click="openReceiptDialog(receipt.id)"
            />
          </td>
          <td width="200px">
            <div class="flex flex-col ml-3">
              <div>
                <span class="text-lg">
                  {{
                    receipt.expenseDate !== null
                      ? $filters.dateFormat(receipt.expenseDate)
                      : '----/--/--'
                  }}
                </span>
                <span class=" text-sm">&nbsp;支出</span>
                <span class="text-green text-lg"
                  ><br />
                  {{ $filters.dateFormat(receipt.createdAt) }}
                </span>
                <span class="text-green text-sm">&nbsp;作成</span>
              </div>
            </div>
          </td>
          <td width="180px">
            <div class="flex flex-col ml-3">
              <div>
                <span class="text-lg">
                  {{ expenseTypesToString(receipt.expenseTypeId) }}
                </span>
              </div>
              <div>
                <div class="flex flex-row items-center">
                  <user-circle-icn
                    width="20"
                    height="20"
                    class="green-icon-user"
                  />
                  <span
                    class="ml-1 text-green"
                    :class="
                      receipt.user.uname === currentUser.uname
                        ? 'font-bold'
                        : ''
                    "
                  >
                    {{ receipt.user.uname }}
                  </span>
                </div>
              </div>
            </div>
          </td>
          <td width="100px" class="py-4" style="text-align:end;">
            <span class="mr-2" style="white-space: nowrap;">
              {{
                receipt.displayAmount
                  ? receipt.displayAmount.toLocaleString() + ' 円'
                  : ''
              }}
            </span>

            <br />
            <div class="flex" style="flex-direction: row-reverse;">
              <ul v-for="total of receipt.option.total" :key="total">
                <approved-icn
                  v-if="
                    (total === 3 && receipt.option.current === 3) ||
                      (total === 2 && receipt.option.current >= 2) ||
                      (total === 1 && receipt.option.current >= 1)
                  "
                  class="mr-1"
                  width="20px"
                  height="20px"
                >
                </approved-icn
                ><approved-not-circle-icn
                  v-if="
                    (total === 3 && receipt.option.current !== 3) ||
                      (total === 2 && receipt.option.current <= 1) ||
                      (total === 1 && receipt.option.current === 0)
                  "
                  class="mr-1 border-2 border-dotted light-green-icon"
                  style="border-radius: 10px; border-color:#08D61C;"
                  width="20px"
                  height="20px"
                ></approved-not-circle-icn>
              </ul>
            </div>
          </td>
          <td class="py-4">
            <div class="flex flex-col">
              <div>
                <span class="text-ms">{{ receipt.expenseContent1 }}</span>
              </div>
              <div>
                <span class="text-ms">{{ receipt.expenseContent2 }}</span>
              </div>
            </div>
          </td>
          <td width="20px" class="text-center">
            <Menu as="div" class="relative flex text-left">
              <MenuButton>
                <actions-icn width="20px" height="20px" class="green-icon" />
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <MenuItems
                  class="absolute right-8 mt-0 origin-top-right bg-white divide-y divide-gray-100 shadow-lg focus:outline-none"
                  style="border-radius: 20px; width: 168px; padding: 2px 2px; top:-10px;"
                >
                  <div
                    class="border-4 border-green"
                    style="border-radius: 20px;"
                  >
                    <MenuItem
                      v-slot="{ active }"
                      class="text-green disabled:bg-black-lightest"
                      style="border-radius: 16px;"
                      :disabled="currentUser.role_id !== 1"
                    >
                      <button
                        @click="showDeletionConfirmation(receipt)"
                        :class="[
                          active
                            ? 'bg-green-lightest text-green'
                            : 'text-gray-900',
                          'group flex rounded-md items-center w-full px-2 py-2 text-sm',
                        ]"
                      >
                        <div class="flex flex-row items-center">
                          <TrashIcn
                            fill-rule="evenodd"
                            class="mr-1 green-icon"
                            width="20px"
                            height="20px"
                            :disabled="currentUser.role_id !== 1"
                          />
                          <span class="text-green text-lg">削除</span>
                        </div>
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </td>
        </tr>
      </template>
    </app-table>
    <AppConfirm color="green" />
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import AppTable from '@/components/ui/AppTable'
import AppReceiptStatusIndicator from '@/components/ui/AppReceiptStatusIndicator'
import UserCircleIcn from '@/assets/icons/user-circle.svg?inline'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import ActionsIcn from '@/assets/icons/actions.svg?inline'
import { useKeywordSearch } from '@/services/useKeywordSearch'
import { useReceipts } from '@/services/receiptsYokohama/useReceipts'
import { useReceiptDialog } from '@/services/receiptsYokohama/useReceiptDialog'
import TrashIcn from '@/assets/icons/trash.svg?inline'
import ApprovedIcn from '@/assets/icons/approved.svg?inline'
import ApprovedNotCircleIcn from '@/assets/icons/approved-not-circle.svg?inline'
import AppConfirm from '@/components/ui/AppConfirm'
import { useConfirmation } from '@/services/useConfirmation'
import { useCurrentUser } from '@/services/useCurrentUser'
import { expenseTypesToString } from '@/services/useExpenseType'
import { useDenominationSelect } from '@/services/useDenominationSelect'
import { useInputSelectSearch } from '@/services/useInputSelectSearch'

export default {
  name: 'ReceiptsTable',

  components: {
    AppTable,
    AppReceiptStatusIndicator,
    UserCircleIcn,
    ActionsIcn,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    TrashIcn,
    AppConfirm,
    ApprovedIcn,
    ApprovedNotCircleIcn,
  },

  setup() {
    const { keyword } = useKeywordSearch()
    const currentReceipt = ref(null)
    const { receipts: receipts, filteredStatus, isReversedSort } = useReceipts()

    const { currentUser } = useCurrentUser()
    const {
      toggleShowConfirmation,
      isConfirmed,
      showConfirmation,
    } = useConfirmation()
    const { selectedDenomination } = useDenominationSelect()
    const { isInput } = useInputSelectSearch()

    onMounted(() => {
      filteredStatus.value = [] // reset filtered status
    })

    const filteredReceipts = computed(() => {
      if (isReversedSort && isReversedSort.value == true) {
        return filterByStatus(filterByKeyword(receipts.value)).reverse()
      } else {
        return filterByStatus(filterByKeyword(receipts.value))
      }
    })

    const filteredReceiptsBySelect = computed(() => {
      if (isReversedSort && isReversedSort.value == true) {
        return filterByStatus(filterByDenomination(receipts.value)).reverse()
      } else {
        return filterByStatus(filterByDenomination(receipts.value))
      }
    })

    const filterByStatus = receipts => {
      if (filteredStatus.value.length) {
        return receipts.filter(item =>
          filteredStatus.value.includes(item.option.viewType),
        )
      }
      return receipts
    }

    const filterByKeyword = receipts => {
      return receipts.filter(
        item =>
          keyword.value.length == 0 ||
          (item['expenseContent1'] &&
            item['expenseContent1'].includes(keyword.value)) ||
          (item['expenseContent2'] &&
            item['expenseContent2'].includes(keyword.value)) ||
          (item['user'].uname && item['user'].uname.includes(keyword.value)) ||
          (item['expenseTypeId'] &&
            expenseTypesToString(item['expenseTypeId']).includes(
              keyword.value,
            )),
      )
    }

    const filterByDenomination = receipts => {
      return receipts.filter(
        item =>
          selectedDenomination.value == 0 ||
          item.denominationId == selectedDenomination.value.denominationId,
      )
    }

    /**
     * 諸経費削除確認画面を表示する
     *
     * @param {object} item receipt
     */
    const showDeletionConfirmation = item => {
      currentReceipt.value = item
      toggleShowConfirmation(true)
    }

    /**
     * 諸経費を削除する
     */
    const deleteItem = async () => {
      // execute
      const { deleteReceipt } = useReceipts()
      const receiptId = currentReceipt?.value?.id ?? null
      if (receiptId !== null) {
        await deleteReceipt(receiptId)
      }

      // tear down
      currentReceipt.value = null
      toggleShowConfirmation(false)
    }

    watch(isConfirmed, isConfirmed => {
      if (isConfirmed) {
        deleteItem()
      }
    })
    watch(showConfirmation, (showConfirmation, prevShowConfirmation) => {
      const isClosed = prevShowConfirmation && !showConfirmation
      if (isClosed) {
        currentReceipt.value = null
      }
    })

    return {
      ...useReceiptDialog(),
      ...useReceipts(),
      filterByKeyword,
      showDeletionConfirmation,
      expenseTypesToString,
      currentReceipt,
      filteredReceipts,
      currentUser,
      filterByDenomination,
      filteredReceiptsBySelect,
      isInput,
    }
  },
}
</script>

<style lang="scss"></style>
